import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Package, Clock, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  name: '',
  branch_ids: [],
  all_products: true,
  product_ids: [],
  unit_type: 'adet',
  inactive_days: '',
};

const InactiveWarningSettings = ({ readOnly = false }) => {
  const [rules, setRules] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
    'Content-Type': 'application/json',
  });

  const loadRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/purchasing/warning-rules?rule_type=hareketsiz_stok`, { headers: getAuthHeaders() });
      setRules(res.data || []);
    } catch (e) {
      console.error('Hareketsiz stok uyarı kuralları yüklenemedi:', e);
    }
  }, []);

  const loadBranches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/branches`, { headers: getAuthHeaders() });
      setBranches(res.data || []);
    } catch (e) {
      console.error('Şubeler yüklenemedi:', e);
    }
  }, []);

  useEffect(() => {
    loadRules();
    loadBranches();
  }, [loadRules, loadBranches]);

  // Seçili şubelerin ürünlerini yükle (birleştir + tekilleştir)
  useEffect(() => {
    const fetchProducts = async () => {
      if (!form.branch_ids || form.branch_ids.length === 0) {
        setProducts([]);
        return;
      }
      try {
        const results = await Promise.all(
          form.branch_ids.map((bid) =>
            axios.get(`${API}/api/depot/stok-kartlari?branch_id=${bid}`, { headers: getAuthHeaders() })
              .then((r) => r.data || []).catch(() => [])
          )
        );
        const merged = [];
        const seen = new Set();
        results.flat().forEach((p) => {
          const key = p.id || p.product_name;
          if (p.product_name && !seen.has(key)) {
            seen.add(key);
            merged.push({ id: p.id || p.product_name, product_name: p.product_name, unit: p.unit });
          }
        });
        merged.sort((a, b) => (a.product_name || '').localeCompare(b.product_name || '', 'tr'));
        setProducts(merged);
      } catch (e) {
        console.error('Ürünler yüklenemedi:', e);
      }
    };
    fetchProducts();
  }, [form.branch_ids]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      name: rule.name || '',
      branch_ids: rule.branch_ids || [],
      all_products: rule.all_products !== false,
      product_ids: rule.product_ids || [],
      unit_type: rule.unit_type || 'adet',
      inactive_days: rule.inactive_days ?? '',
    });
    setModalOpen(true);
  };

  const toggleBranch = (bid) => {
    setForm((prev) => {
      const exists = prev.branch_ids.includes(bid);
      const branch_ids = exists ? prev.branch_ids.filter((x) => x !== bid) : [...prev.branch_ids, bid];
      return { ...prev, branch_ids };
    });
  };

  const toggleProduct = (pid) => {
    setForm((prev) => {
      const exists = prev.product_ids.includes(pid);
      const product_ids = exists ? prev.product_ids.filter((x) => x !== pid) : [...prev.product_ids, pid];
      return { ...prev, product_ids };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Lütfen ayar ismi girin');
      return;
    }
    if (form.branch_ids.length === 0) {
      toast.error('En az bir şube seçmelisiniz');
      return;
    }
    if (!form.all_products && form.product_ids.length === 0) {
      toast.error('En az bir ürün seçin veya "Tüm Ürünler" seçeneğini işaretleyin');
      return;
    }
    const payload = {
      rule_type: 'hareketsiz_stok',
      name: form.name.trim(),
      branch_ids: form.branch_ids,
      all_products: form.all_products,
      product_ids: form.all_products ? [] : form.product_ids,
      unit_type: form.unit_type,
      inactive_days: form.inactive_days === '' ? null : Number(form.inactive_days),
    };
    setLoading(true);
    try {
      if (editingId) {
        await axios.put(`${API}/api/purchasing/warning-rules/${editingId}`, payload, { headers: getAuthHeaders() });
        toast.success('Hareketsiz stok uyarı ayarı güncellendi');
      } else {
        await axios.post(`${API}/api/purchasing/warning-rules`, payload, { headers: getAuthHeaders() });
        toast.success('Hareketsiz stok uyarı ayarı oluşturuldu');
      }
      setModalOpen(false);
      loadRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`"${rule.name}" ayarını silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/api/purchasing/warning-rules/${rule.id}`, { headers: getAuthHeaders() });
      toast.success('Ayar silindi');
      loadRules();
    } catch (e) {
      toast.error('Silme başarısız');
    }
  };

  const branchName = (bid) => branches.find((b) => b.id === bid)?.name || bid;
  const unitSuffix = (t) => ('adet');

  return (
    <div className="space-y-4" data-testid="inactive-warning-settings">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Hareketsiz Stok Uyarı Ayarları</h3>
          <p className="text-sm text-gray-500">Şube ve ürün bazında hareketsizlik eşiklerini tanımlayın.</p>
        </div>
        {!readOnly && (
          <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700" data-testid="add-inactive-rule-btn">
            <Plus className="h-4 w-4 mr-2" /> Yeni Hareketsiz Stok Uyarı Ayarı
          </Button>
        )}
      </div>

      {/* Kural Listesi */}
      {rules.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white py-14 text-center">
          <Package className="mb-3 h-10 w-10 text-gray-300" />
          <p className="text-base font-medium text-gray-700">Henüz hareketsiz stok uyarı ayarı yok</p>
          <p className="mt-1 text-sm text-gray-400">Yeni bir hareketsiz stok uyarı ayarı ekleyerek başlayın.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="inactive-rules-list">
          {rules.map((rule) => (
            <div key={rule.id} className="rounded-xl border border-gray-200 bg-white p-4" data-testid={`inactive-rule-${rule.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800">{rule.name}</span>
                    <Badge variant="outline" className="text-[11px]">
                      <Clock className="h-3 w-3 mr-1" />
                      Gün
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="text-gray-500 flex items-center gap-1"><Building2 className="h-3 w-3" /> Şubeler:</span>
                    {(rule.branch_ids || []).map((bid) => (
                      <Badge key={bid} className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[11px]">{branchName(bid)}</Badge>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="text-gray-500 flex items-center gap-1"><Package className="h-3 w-3" /> Ürünler:</span>
                    {rule.all_products ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[11px]">Tüm Ürünler</Badge>
                    ) : (
                      <span className="text-gray-700">{(rule.product_ids || []).length} ürün</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-gray-600 pt-1">
                    <span>Hareketsizlik Süresi: <b>{rule.inactive_days ?? '-'}{rule.inactive_days != null ? ' Gün' : ''}</b></span>
                  </div>
                </div>
                {!readOnly && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(rule)} data-testid={`edit-inactive-rule-${rule.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDelete(rule)} data-testid={`delete-inactive-rule-${rule.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ekle/Düzenle Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inactive-rule-modal">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Hareketsiz Stok Uyarı Ayarını Düzenle' : 'Yeni Hareketsiz Stok Uyarı Ayarı'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* İsim */}
            <div className="space-y-2">
              <Label>Ayar İsmi *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="örn. Kritik Malzeme Gecikmesi"
                data-testid="purchase-rule-name"
              />
            </div>

            {/* Şubeler */}
            <div className="space-y-2">
              <Label>Şubeler * <span className="text-xs text-gray-400">(bakılacak şubeleri seçin)</span></Label>
              <div className="border rounded-md p-3 max-h-40 overflow-y-auto grid grid-cols-2 gap-2" data-testid="inactive-rule-branches">
                {branches.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.branch_ids.includes(b.id)} onCheckedChange={() => toggleBranch(b.id)} data-testid={`branch-check-${b.id}`} />
                    {b.name}
                  </label>
                ))}
              </div>
              {form.branch_ids.length > 0 && <p className="text-xs text-gray-500">{form.branch_ids.length} şube seçildi</p>}
            </div>

            {/* Ürünler */}
            <div className="space-y-2">
              <Label>Ürünler</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={form.all_products} onCheckedChange={(v) => setForm((p) => ({ ...p, all_products: !!v }))} data-testid="all-products-check" />
                  Tüm Ürünler
                </label>
              </div>
              {!form.all_products && (
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2" data-testid="inactive-rule-products">
                  {form.branch_ids.length === 0 ? (
                    <p className="text-sm text-gray-400">Önce şube seçin</p>
                  ) : products.length === 0 ? (
                    <p className="text-sm text-gray-400">Seçili şubelerde ürün bulunamadı</p>
                  ) : (
                    products.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={form.product_ids.includes(p.id)} onCheckedChange={() => toggleProduct(p.id)} data-testid={`product-check-${p.id}`} />
                        {p.product_name}{p.unit ? <span className="text-gray-400">({p.unit})</span> : null}
                      </label>
                    ))
                  )}
                </div>
              )}
              {!form.all_products && form.product_ids.length > 0 && (
                <p className="text-xs text-gray-500">{form.product_ids.length} ürün seçildi</p>
              )}
            </div>

            {/* Eşikler */}
                        <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>Hareketsizlik Süresi (Gün)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.inactive_days}
                  onChange={(e) => setForm((p) => ({ ...p, inactive_days: e.target.value }))}
                  placeholder={'Örn: 30'}
                  data-testid="inactive-rule-inactive-days"
                />
                <p className="text-xs text-gray-500">Ürün rafta kaç gün hareketsiz (giriş/çıkış yok) kalırsa uyarı verileceğini belirleyin.</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Vazgeç</Button>
            <Button onClick={handleSave} disabled={loading} className="bg-amber-600 hover:bg-amber-700" data-testid="save-inactive-rule-btn">
              {editingId ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InactiveWarningSettings;
